import type * as React from "react";
import { useCallback } from "react";

import ProductImage, { type ProductImageWidths } from "~/domains/product/components/ProductImage";
import classNames from "classnames";
import Parameter from "~/common/components/Parameter";
import { type HousingProduct, HousingProductType } from "~/domains/product/types/ProductData";
import { Link, LinkButton, LinkType } from "~/common/components/Link";
import ProductDesignation from "~/features/productDetails/components/ProductDesignation";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { Card } from "~/features/configurator/Linear/components/Card";
import { useBoolMap } from "~/common/hooks/useBoolMap";
import { toMapFn } from "~/common/utils/reducers";
import { Collapse, Stack } from "~/common/mui";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { NewVersionMessage } from "~/common/components/Messages/NewVersionMessage";
import { OldDesignationParameter } from "~/domains/product/components/ProductParams";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { MAX_QUANTITY } from "~/utils/constants";
import { userIsCustomer, userIsDistributor } from "~/domains/user/utils/roles";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { StockLevelSection } from "~/features/productDetails/components/AddToCartCard/StockLevelSection";
import { getPackagingLabelAtLineItem } from "~/features/cart/cart/utils/cartUtils";
import { useFormContext } from "react-hook-form";
import type { AddToCartFormDraft } from "~/features/addToCart/hooks/useAddToCartForm";
import { useAddToCartDerived } from "~/features/addToCart/hooks/AddToCartDerivedContext";
import { AddToCartService } from "~/features/addToCart/AddToCartService";
import { Translation } from "~/app/i18n/Translation";
import { canSeeListPrice } from "~/utils/features";
import { useOnMount } from "~/common/hooks/useOnMount";
import { isSpecialPrice } from "~/features/productDetails/redux/productDetailsSelectors";
import { useTranslation } from "react-i18next";
import { CheckedCircle } from "~/common/components/CheckedCircle/CheckedCircle";
import { NetPrice } from "~/features/productDetails/components/ProductSummary/components/NetPrice";
import { ListPrice } from "~/features/productDetails/components/ProductSummary/components/ListPrice";
import { PackagingCellInput } from "~/features/cart/cart/components/PackagingCell";
import EditIcon from "@mui/icons-material/Edit";

type HeadlineLevels = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";

interface Props {
  housingProducts: HousingProduct[];
  headlineLevel?: HeadlineLevels;
  className?: string;
}

export const OptionalPartsSection = ({ housingProducts, headlineLevel = "p", className }: Props) => {
  const Headline = headlineLevel;
  const { t } = useTranslation();

  const canExpand = userIsCustomer();

  const { map: expandedItemMap, toggle: toggleExpanded, replace } = useBoolMap();
  const isAllOpen =
    housingProducts.length === Object.keys(expandedItemMap).length &&
    housingProducts.every((item) => expandedItemMap[item.id]);

  const btnIcon = <Icon type={isAllOpen ? IconType.ChevronUp : IconType.ChevronDown} />;

  const toggleAll = useCallback(() => {
    replace(
      toMapFn(
        housingProducts,
        (item) => item.id,
        () => !isAllOpen,
      ),
    );
  }, [housingProducts, isAllOpen, replace]);

  useOnMount(() => {
    if (userIsDistributor()) {
      replace(
        toMapFn(
          housingProducts,
          (product) => product.id,
          () => true,
        ),
      );
    }
  });

  return (
    <div className={classNames("scha__optional-parts-section", className)}>
      <Headline className="scha__optional-parts-section__title">
        {t("page.productDetails.housing.optional.title")}
      </Headline>
      <div className={"d-flex flex-justify-between mb-30"}>
        <p className="label-large m-0">{t("page.productDetails.housing.optional.subtitle")}</p>
        {canExpand && (
          <LinkButton onClick={toggleAll} iconBefore={btnIcon}>
            {t(`page.productDetails.housing.optional.${isAllOpen ? "closeall" : "openall"}`)}
          </LinkButton>
        )}
      </div>
      <div className="scha__optional-parts-section__items">
        {housingProducts.map((housingProduct, index) => (
          <OptionalPartsItem
            key={housingProduct.id}
            index={index}
            housingProduct={housingProduct}
            expanded={expandedItemMap[housingProduct.id]}
            onExpand={() => toggleExpanded(housingProduct.id)}
          />
        ))}
      </div>
    </div>
  );
};

interface ItemProps {
  housingProduct: HousingProduct;
  index: number;
  className?: string;
  expanded?: boolean;
  onExpand?: () => void;
}

const IMAGE_WIDTHS: ProductImageWidths = {
  product: "120px",
  manufacturer: "32px",
  brand: "27px",
  gap: "10px",
};

const OptionalPartsItem = ({ housingProduct, index: _index, className, expanded, onExpand }: ItemProps) => {
  const { t } = useTranslation();
  const formProps = useFormContext<AddToCartFormDraft>();
  const { optionalParts: derivedOptionalParts } = useAddToCartDerived();

  const priceReference = formProps.watch("priceReference");
  const optionalPartViewModel = derivedOptionalParts.byId[housingProduct.id];

  if (!optionalPartViewModel) {
    return null;
  }

  const { draft, derived } = optionalPartViewModel;
  const { quantity, selected: active, selectedPackCode } = draft;
  const descriptor = derived.descriptor;

  const isSpecial = isSpecialPrice(descriptor, "");

  const updateQty = (newVal: number) => {
    formProps.setValue(`optionalParts.${housingProduct.id}.quantity`, newVal, { shouldDirty: true, shouldTouch: true });
  };
  const toggleSelected = () =>
    formProps.setValue(`optionalParts.${housingProduct.id}.selected`, !active, {
      shouldDirty: true,
      shouldTouch: true,
    });

  // Initial pack code selection is now handled by useAddToCartForm hook

  const product = descriptor.base;
  const isHousing = housingProduct.type === HousingProductType.HOUSING;

  let designation = (
    <ProductDesignation
      size={"title-medium"}
      className={"scha__optional-parts-item__designation"}
      designation={product.designation}
      isPreferred={product.preferred}
      noCssColor={true}
    />
  );

  const canExpand = userIsCustomer();

  if (!isHousing && product.url) {
    designation = (
      <Link variant={LinkType.NAVIGATION} href={product.url} target={"_blank"}>
        {designation}
      </Link>
    );
  }

  const btnIcon = <Icon type={expanded ? IconType.ChevronUp : IconType.ChevronDown} />;

  const toggleExpandCb = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onExpand) {
      onExpand();
    }
  };

  const selectedPackage = derived.selectedPack;

  let primaryWarehouse = "-";
  if (selectedPackage) {
    primaryWarehouse = selectedPackage.totalWarehouse.name;
  }
  const packSize = selectedPackage?.packSize ?? 1;

  let label: string = "-";
  if (selectedPackage) {
    label = getPackagingLabelAtLineItem({
      t: t,
      productDescriptor: descriptor,
      quantity,
      packSize,
      packCode: selectedPackCode,
    });
  }

  const openEditModal = () => {
    AddToCartService.openEditPartWizard({
      productCode: descriptor.base.code,
      editItemId: housingProduct.id,
      initialData: {
        quantity,
        selectedPackCode,
        priceReference,
      },
      restrictionQuery: "",
      onSubmit: async (data) => {
        if (data.formValues.quantity !== quantity) {
          formProps.setValue(`optionalParts.${housingProduct.id}.quantity`, data.formValues.quantity, {
            shouldDirty: true,
            shouldTouch: true,
          });
        }
        if (data.formValues.selectedPackCode !== selectedPackCode) {
          formProps.setValue(`optionalParts.${housingProduct.id}.selectedPackCode`, data.formValues.selectedPackCode, {
            shouldDirty: true,
            shouldTouch: true,
          });
        }
        return true;
      },
    });
  };

  const listPriceVisible = canSeeListPrice({ product: descriptor, isSpecialPrice: isSpecial });

  return (
    <Card
      className={classNames("scha__optional-parts-item", className, isHousing && "scha__optional-parts-item--housing")}
      data-test-id={`pdp-housing-${housingProduct.id}`}
      active={active}
      onClick={toggleSelected}
    >
      <Stack gap={2}>
        <CheckedCircle className={"scha__optional-parts-item__checked"} checked={active} />

        <ProductImage product={product} widths={IMAGE_WIDTHS} />

        {designation}

        <p className="scha__optional-parts-item__short-description">
          <Translation {...product.shortDescription} />
        </p>

        <div className="scha__optional-parts-item__description truncate-text">{product.description}</div>

        {expanded && <NewVersionMessage baseProduct={product} />}
        {expanded && <OldDesignationParameter product={product} label={t("productDetailPage.oldDesignation")} />}

        {descriptor.isPriceLoading && <CircleSpinner />}
        {descriptor.prices && (
          <Stack gap={2}>
            {userHasPermission(Permission.PRICE) && (
              <NetPrice
                quantity={quantity}
                product={descriptor}
                packCode={selectedPackCode}
                sx={{
                  '& [data-test-id="value"]': {
                    ...getFontStyle(FontSize.TitleLarge),
                    fontWeight: "bold",
                  },
                  '& [data-test-id="unit"]': {
                    ...getFontStyle(FontSize.LabelLarge),
                  },
                }}
              />
            )}
            {listPriceVisible && <ListPrice product={descriptor} packCode={""} />}
          </Stack>
        )}
        {descriptor.isAvailabilityLoading && <CircleSpinner />}
        {descriptor.availability && (
          <StockLevelSection
            productDescriptor={descriptor}
            quantity={quantity}
            onCheckAvailabilityClick={openEditModal}
            checkAvailabilityDisabled={false}
          />
        )}

        <div>
          <Collapse in={!expanded}>
            <Parameter label={t("input.quantity")}>{quantity}</Parameter>
          </Collapse>

          <Collapse in={expanded}>
            <Stack gap={2}>
              <div onClick={NO_PROPAGATE}>
                <QuantityInput
                  max={MAX_QUANTITY}
                  min={packSize}
                  value={quantity}
                  onValueChange={updateQty}
                  step={packSize}
                />
              </div>
              <div onClick={NO_PROPAGATE}>
                <PackagingCellInput
                  disabled={descriptor.isLoading || !descriptor.availability}
                  value={label}
                  helperText={t(primaryWarehouse)}
                  onClick={openEditModal}
                  icon={<EditIcon />}
                />
              </div>
            </Stack>
          </Collapse>
        </div>

        {canExpand && (
          <LinkButton iconBefore={btnIcon} onClick={toggleExpandCb} className="scha__optional-parts-item__see-more">
            {t(`page.pdp.housing.${expanded ? "closeDetails" : "openDetails"}`)}
          </LinkButton>
        )}
      </Stack>
    </Card>
  );
};

const NO_PROPAGATE = (e: React.MouseEvent) => e.stopPropagation();
